import { type Manifest, type MediaItem, choosePhoto, fixtureManifest } from '@seemantham/core';

/**
 * The Worker serves the real manifest once the pipeline has published a
 * release. Until then the viewer runs on the fixture, which carries the real
 * shape of the day with placeholder imagery.
 */
export async function loadManifest(fetchImpl: typeof fetch = fetch): Promise<Manifest> {
  try {
    const res = await fetchImpl('/api/manifest/index.json', { credentials: 'same-origin' });
    if (res.ok) return (await res.json()) as Manifest;
    if (res.status === 401) throw new LockedError();
  } catch (err) {
    if (err instanceof LockedError) throw err;
  }
  return fixtureManifest();
}

export class LockedError extends Error {
  constructor() {
    super('locked');
    this.name = 'LockedError';
  }
}

import { thumbHashToDataURL } from 'thumbhash';

const placeholders = new Map<string, string>();

/** ThumbHash decodes to a blurred stand-in that paints before the photo lands. */
export function placeholder(thumb: string | undefined): string | undefined {
  if (!thumb) return undefined;
  // The fixture carries CSS gradients rather than a hash; pass those through.
  if (thumb.startsWith('radial-gradient') || thumb.startsWith('linear-gradient')) return thumb;
  const cached = placeholders.get(thumb);
  if (cached) return cached;
  try {
    const bytes = Uint8Array.from(atob(thumb), (c) => c.charCodeAt(0));
    const url = `url(${thumbHashToDataURL(bytes)})`;
    placeholders.set(thumb, url);
    return url;
  } catch {
    return undefined;
  }
}

/**
 * The published rendition, with the blurred stand-in painted under it so a tile
 * is never empty on a slow connection (US-09).
 */
export function background(thumb: string | undefined, key?: string): string {
  const under = placeholder(thumb);
  if (key) return under ? `url(/${key}), ${under}` : `url(/${key})`;
  return under ?? 'var(--raised)';
}

/**
 * The still to paint for any item. A clip's renditions start with its video
 * files, so reaching for renditions[0] put an MP4 in a CSS background and left
 * the tile showing nothing but the blur. Choosing by rendered width instead
 * picks the poster for a clip and the right size for a photo.
 */
export function stillKey(item: MediaItem | undefined, cssWidth: number): string | undefined {
  if (!item) return undefined;
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  return choosePhoto(item.renditions, cssWidth, dpr)?.key;
}

/**
 * How wide a grid tile really renders, mirroring app.css: two columns inside the
 * 24px gutters on a phone, three columns beside the 320px sidebar from 900px up.
 */
export function tileWidth(viewport = typeof window === 'undefined' ? 390 : window.innerWidth): number {
  if (viewport >= 900) return Math.round((viewport - 320 - 84 - 36) / 3);
  return Math.round((viewport - 48 - 14) / 2);
}

/** The full-bleed width a hero or a photo stage renders at. */
export function stageWidth(): number {
  return typeof window === 'undefined' ? 390 : window.innerWidth;
}
